import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationCenterService, UserNotificationDto, NotificationPreferenceDto, NotificationCategoryConfigDto } from './notification-center.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss']
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  notifications: UserNotificationDto[] = [];
  unreadCount = 0;
  loading = false;
  
  // Pagination
  skip = 0;
  take = 20;
  total = 0;
  hasMore = false;

  // Filters
  showOnlyUnread = true;

  // Preferences
  showPreferences = false;
  preferences: NotificationPreferenceDto = {
    InAppEnabled: true,
    EmailEnabled: true,
    SMSEnabled: true
  };
  savingPreferences = false;

  // Per-category preferences
  categoryPreferences: NotificationCategoryConfigDto[] = [];
  savingCategoryPreferences = false;

  // Subscriptions
  private subscriptions = new Subscription();

  constructor(
    private notificationService: NotificationCenterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadNotifications();
    this.loadUnreadCount();
    this.loadPreferences();
    this.loadCategoryPreferences();

    // Subscribe to real-time notifications
    this.subscriptions.add(
      this.notificationService.notificationReceived$.subscribe(notification => {
        this.notifications.unshift(notification);
        this.total++;
      })
    );

    // Subscribe to unread count changes
    this.subscriptions.add(
      this.notificationService.unreadCountChanged$.subscribe(count => {
        this.unreadCount = count;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadNotifications(): void {
    this.loading = true;
    this.notificationService.getNotifications(this.skip, this.take, !this.showOnlyUnread).subscribe({
      next: (result) => {
        // Ensure result and notifications are defined
        const notifications = result?.notifications || [];
        const total = result?.total || 0;
        
        if (this.skip === 0) {
          this.notifications = notifications;
        } else {
          this.notifications = [...this.notifications, ...notifications];
        }
        this.total = total;
        this.hasMore = this.notifications.length < this.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load notifications', error);
        this.notifications = [];
        this.total = 0;
        this.hasMore = false;
        this.loading = false;
      }
    });
  }

  loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (count) => {
        this.unreadCount = count;
      },
      error: (error) => {
        console.error('Failed to load unread count', error);
      }
    });
  }

  loadPreferences(): void {
    this.notificationService.getPreferences().subscribe({
      next: (preferences) => {
        this.preferences = preferences;
      },
      error: (error) => {
        console.error('Failed to load preferences', error);
      }
    });
  }

  loadCategoryPreferences(): void {
    this.notificationService.getCategoryPreferences().subscribe({
      next: (prefs) => {
        this.categoryPreferences = prefs;
      },
      error: (error) => {
        console.error('Failed to load category preferences', error);
      }
    });
  }

  // Grouped views for the template
  get crmCategories(): NotificationCategoryConfigDto[] {
    return this.categoryPreferences.filter(c => c.requiredModuleId === 31);
  }
  get projectCategories(): NotificationCategoryConfigDto[] {
    return this.categoryPreferences.filter(c => c.requiredModuleId === 40);
  }
  get hmsCategories(): NotificationCategoryConfigDto[] {
    return this.categoryPreferences.filter(c => c.requiredModuleId === 35);
  }
  get handbookCategories(): NotificationCategoryConfigDto[] {
    return this.categoryPreferences.filter(c => c.requiredModuleId === 41);
  }
  get newsCategories(): NotificationCategoryConfigDto[] {
    return this.categoryPreferences.filter(c => c.requiredModuleId === 6);
  }

  onMarkAsRead(notification: UserNotificationDto): void {
    this.notificationService.markAsRead(notification.NotificationId).subscribe({
      next: (success) => {
        if (success) {
          notification.IsRead = true;
          notification.ReadDate = new Date();
        }
      },
      error: (error) => {
        console.error('Failed to mark as read', error);
      }
    });
  }

  onNavigate(notification: UserNotificationDto): void {
    if (!notification.IsRead) {
      this.onMarkAsRead(notification);
    }
    if (notification.NavigationUrl) {
      this.router.navigateByUrl(notification.NavigationUrl);
    }
  }

  onMarkAllAsRead(): void {
    if (!confirm('Markere alle varsler som lest?')) {
      return;
    }

    this.notificationService.markAllAsRead().subscribe({
      next: (success) => {
        if (success) {
          this.notifications.forEach(n => {
            n.IsRead = true;
            n.ReadDate = new Date();
          });
        }
      },
      error: (error) => {
        console.error('Failed to mark all as read', error);
      }
    });
  }

  onDelete(notification: UserNotificationDto): void {
    if (!confirm('Er du sikker på at du vil slette dette varselet?')) {
      return;
    }

    this.notificationService.deleteNotification(notification.NotificationId).subscribe({
      next: (success) => {
        if (success) {
          this.notifications = this.notifications.filter(n => n.NotificationId !== notification.NotificationId);
          this.total--;
        }
      },
      error: (error) => {
        console.error('Failed to delete notification', error);
      }
    });
  }

  onToggleFilter(): void {
    this.showOnlyUnread = !this.showOnlyUnread;
    this.skip = 0;
    this.loadNotifications();
  }

  onLoadMore(): void {
    this.skip += this.take;
    this.loadNotifications();
  }

  onRefresh(): void {
    this.skip = 0;
    this.loadNotifications();
    this.loadUnreadCount();
  }

  onTogglePreferences(): void {
    this.showPreferences = !this.showPreferences;
  }

  get atLeastOneGlobalEnabled(): boolean {
    return this.preferences.InAppEnabled || this.preferences.EmailEnabled || this.preferences.SMSEnabled;
  }

  onSavePreferences(): void {
    if (!this.atLeastOneGlobalEnabled) return;
    this.savingPreferences = true;
    this.notificationService.savePreferences(this.preferences).subscribe({
      next: (success) => {
        if (success) {
          this.showPreferences = false;
        }
        this.savingPreferences = false;
      },
      error: (error) => {
        console.error('Failed to save preferences', error);
        this.savingPreferences = false;
      }
    });
  }

  onSaveCategoryPreferences(): void {
    this.savingCategoryPreferences = true;
    this.notificationService.saveCategoryPreferences(this.categoryPreferences).subscribe({
      next: () => {
        this.savingCategoryPreferences = false;
      },
      error: (error) => {
        console.error('Failed to save category preferences', error);
        this.savingCategoryPreferences = false;
      }
    });
  }

  formatDateTime(date?: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Akkurat nå';
    if (minutes < 60) return `${minutes} min siden`;
    if (hours < 24) return `${hours} timer siden`;
    if (days < 7) return `${days} dager siden`;
    
    return d.toLocaleDateString('no-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  getPriorityClass(priority?: string): string {
    switch (priority) {
      case 'Urgent':
        return 'priority-urgent';
      case 'High':
        return 'priority-high';
      case 'Normal':
        return 'priority-normal';
      case 'Low':
        return 'priority-low';
      default:
        return 'priority-normal';
    }
  }

  getPriorityIcon(priority?: string): string {
    switch (priority) {
      case 'Urgent':
        return 'bi-exclamation-triangle-fill';
      case 'High':
        return 'bi-exclamation-circle-fill';
      case 'Normal':
        return 'bi-info-circle-fill';
      case 'Low':
        return 'bi-info-circle';
      default:
        return 'bi-info-circle';
    }
  }
}
